// 運行実績×収支 集約（中身/TMS・純関数）。trips＋tachographs → 客先別 実コスト収支・実車率・突合率。
// 出所: ace_tms_pipeline.process_month（乗務員コード×運行期間で突合、トリップ内kmを運賃按分、未突合は売上按分）。
// 係数: 人件費率＝乗務員ごと（drivers マスタ rates:[{from,pct}] 適用開始日つき）を常時適用（2026-08-27 一本化）。
// 個別設定が無い乗務員（率未設定・固定給）は「試算用」率（settings/labor.estimatePct）へフォールバック。
export const LABOR_RATE = 0.35 // 試算用率の初期値＝売上×35%（ユーザー指定 2026-07-31。参考: 6月実効0.348）

// 人件費率設定（2026-08-27 一本化。旧「収支に反映」トグルは廃止＝乗務員別率が常に効く）。
// main/各ビューが refreshLabor() 経由で setLabor() に流し込む。rate＝試算用率（フォールバック）、drivers＝乗務員マスタ。
let LABOR_CFG = { rate: null, drivers: null }
export function setLabor(cfg) { LABOR_CFG = { ...LABOR_CFG, ...(cfg || {}) } }
export function getLabor() { return { ...LABOR_CFG } }

// 歩合率の適用開始日つき解決（レジストリ#5・2026-08-25）。
// 乗務員ドキュメント m から、日付 iso（積日）に有効な歩合率(%)を返す。解決できなければ null（呼び出し側が既定率へ）。
// - m.rates: [{from:'YYYY-MM-DD'|'', pct:number}]。from='' は「当初から」。from <= iso の中で最大の from が勝つ
// - 後方互換: rates に当初(from:'')が無ければ、スカラー m.commissionRate を当初率として扱う
// - 固定給(wageType:'fixed')は対象外（反映式は今後）→ null
export function commissionPctAt(m, iso) {
  if (!m || (m.wageType || 'commission') !== 'commission') return null
  const d = String(iso || '')
  const entries = []
  for (const e of (Array.isArray(m.rates) ? m.rates : [])) {
    if (!e) continue
    const pct = Number(e.pct)
    if (isNaN(pct) || e.pct == null || e.pct === '') continue
    entries.push({ from: String(e.from || ''), pct })
  }
  if (!entries.some((e) => e.from === '')) {
    const sc = m.commissionRate
    if (sc != null && sc !== '' && !isNaN(Number(sc))) entries.push({ from: '', pct: Number(sc) })
  }
  let best = null
  for (const e of entries) {
    if (e.from !== '' && e.from > d) continue // 未来の改定は無効
    if (best === null || e.from > best.from) best = e
  }
  return best ? best.pct : null
}
export const SHARYO_POOL = 23700000 // フリート車両経費（月・旧概算プール／車番が無いデータのフォールバック）
export const VEHICLE_DAY_RATE = { large: 20000, medium: 11000 } // 車両経費の1稼働日あたり日額（大型/中型）
export const VEHICLE_DAYS_PER_MONTH = 20 // 1か月＝20稼働日で換算
export const DIESEL_YEN_PER_L = 150
const vClass = (vt) => /10\s*t|13\s*t|大型|増ト/i.test(String(vt || '')) ? 'large' : 'medium' // 車種→大型/中型（10t/13t/大型/増トン系＝大型・他は中型。ingest/masters.js の判定と整合）

const pad2 = (n) => String(n).padStart(2, '0')
const isoYMD = (dt) => `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`
const ymd = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`
const nthWeekday = (y, m, weekday, n) => {
  const first = new Date(y, m - 1, 1)
  const day = 1 + ((weekday - first.getDay() + 7) % 7) + (n - 1) * 7
  return ymd(y, m, day)
}
const equinoxDay = (y, base) => Math.floor(base + 0.242194 * (y - 1980) - Math.floor((y - 1980) / 4))

// 固定経費の営業日判定：運送は土日祝を稼働日から除外。祝日は年ごとに算出（振替・国民の休日含む）。
const _holidayCache = new Map()
export function japanHolidays(year) {
  const y = Number(year)
  if (_holidayCache.has(y)) return _holidayCache.get(y)
  const set = new Set([
    ymd(y, 1, 1),
    nthWeekday(y, 1, 1, 2),
    ymd(y, 2, 11),
    ymd(y, 2, 23),
    ymd(y, 3, equinoxDay(y, 20.8431)),
    ymd(y, 4, 29),
    ymd(y, 5, 3),
    ymd(y, 5, 4),
    ymd(y, 5, 5),
    nthWeekday(y, 7, 1, 3),
    ymd(y, 8, 11),
    nthWeekday(y, 9, 1, 3),
    ymd(y, 9, equinoxDay(y, 23.2488)),
    nthWeekday(y, 10, 1, 2),
    ymd(y, 11, 3),
    ymd(y, 11, 23),
  ])
  for (const d of [...set]) {
    const dt = new Date(d + 'T00:00:00')
    if (dt.getDay() !== 0) continue
    const n = new Date(dt)
    do { n.setDate(n.getDate() + 1) } while (set.has(isoYMD(n)))
    set.add(isoYMD(n))
  }
  for (const d of [...set]) {
    const dt = new Date(d + 'T00:00:00')
    const mid = new Date(dt); mid.setDate(mid.getDate() + 1)
    const nxt = new Date(dt); nxt.setDate(nxt.getDate() + 2)
    if (!set.has(isoYMD(nxt)) || set.has(isoYMD(mid))) continue
    const g = mid.getDay()
    if (g !== 0 && g !== 6) set.add(isoYMD(mid))
  }
  _holidayCache.set(y, set)
  return set
}

const isBizDay = (dt) => { const g = dt.getDay(); return g !== 0 && g !== 6 && !japanHolidays(dt.getFullYear()).has(isoYMD(dt)) }
export function isBusinessDay(dt) { return isBizDay(dt) }
const asISO = (v) => {
  if (Array.isArray(v)) {
    const y = new Date().getFullYear()
    return `${y}-${pad2(v[0])}-${pad2(v[1])}`
  }
  const m = String(v || '').match(/(\d{4})-(\d{1,2})-(\d{1,2})/)
  return m ? `${m[1]}-${pad2(+m[2])}-${pad2(+m[3])}` : ''
}
// 積日→卸日の営業日(土日祝除外)を 'YYYY-MM-DD' 配列で返す。データ不正時は単日にフォールバック、上限60日でガード。
const bizDaysBetween = (loadStr, unloadStr) => {
  const a = asISO(loadStr); if (!a) return []
  const b = asISO(unloadStr) || a
  const s = new Date(a + 'T00:00:00'), e = new Date(b + 'T00:00:00')
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return isBizDay(s) ? [a] : []
  const out = []; let c = new Date(s), g = 0
  while (c <= e && g < 60) { if (isBizDay(c)) out.push(isoYMD(c)); c.setDate(c.getDate() + 1); g++ }
  return out
}

const key = (d) => (d && d.length === 2 ? d[0] * 100 + d[1] : null)
// ISO(YYYY-MM-DD…) or [m,dd] → [m,dd]
const toMD = (v) => {
  if (Array.isArray(v)) return v
  const m = String(v || '').match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/)
  return m ? [+m[2], +m[3]] : null
}

// 乗務員コード正規化：ゼロ埋め差（日計表"1" と デジタコ"00000001"）を吸収して突合を成立させる。
const normCode = (c) => String(c == null ? '' : c).trim().replace(/^0+(?=\d)/, '')
// 車番正規化：日計側の小数文字列("55.0")・デジタコ側のゼロ埋め("00000055")・整数("55")を同一キーへ統一（同一車両の二重計上を防止）。
const normPlate = (v) => String(v == null ? '' : v).trim().replace(/\.0+$/, '').replace(/^0+(?=\d)/, '')

// 乗務員別 人件費率の解決（収支・給与の共通の口。2026-09-03 給与F1で切り出し。式は従来のまま＝数字は動かない）。
// driversMap が無ければ fallback（試算用率）、あれば適用開始日つき解決（commissionPctAt）。
// 率未設定・固定給は fallback。給与（payroll/monthly.core.js）も必ずここを通す＝式のコピーを作らない。
export function laborRateFor(driversMap, code, iso, fallback) {
  if (!driversMap) return fallback
  const pct = commissionPctAt(driversMap[normCode(code)], iso) // 適用開始日つき解決（#5）
  return pct != null ? pct / 100 : fallback
}

// Firestore/サンプルの trip → 内部形。kind は own/charter/other の3値を保持（other＝乗務員も協力会社も無い＝収支対象外）。
export function normTrip(t) {
  const kind = t.kind === 'own' ? 'own' : (t.kind === 'charter' ? 'charter' : 'other')
  return { driverCode: normCode(t.driverCode), customerName: t.customerFormal || t.customerName || '(未記入)',
    partnerName: t.partnerFormal || t.partnerName || '(協力会社未設定)',
    fare: Number(t.fare) || 0, charterCost: Number(t.charterCost) || 0, tollBill: Number(t.tollBill) || 0, kind,
    carNo: normPlate(t.carNo || t.vehicleNo || ''), vType: String(t.vehicleType || t.vehicleClass || ''),
    from: t.fromPlace || t.origin || '', to: t.toPlace || t.dest || '', driverName: t.driverName || t.ownDriver || t.youshaDriver || '', staff: t.staff || '',
    dLoad: asISO(t.date || t.unkoDate || t.dm), dUnload: asISO(t.unloadDate || t.oroshiDate),
    d: toMD(t.dm || t.date) }
}
export function normTacho(t) {
  return { driverCode: normCode(t.driverCode), driverName: t.driverName || '', sd: toMD(t.sd || t.startAt || t.date), ed: toMD(t.ed || t.endAt || t.date),
    km: Number(t.km != null ? t.km : t.runKm) || 0, loaded: Number(t.loaded != null ? t.loaded : t.loadedKm) || 0, fuel: Number(t.fuel != null ? t.fuel : t.fuelLiter) || 0,
    plate: normPlate(t.plate || t.vehicleNo || ''), name: t.vehicleName || normPlate(t.plate || t.vehicleNo || ''), tollActual: Number(t.tollActual) || 0 }
}

export function computeOperations(rawTrips, rawTachos, opt = {}) {
  const laborDrivers = opt.drivers ?? LABOR_CFG.drivers ?? null
  const labor = opt.labor ?? LABOR_CFG.rate ?? LABOR_RATE // 試算用率（個別設定が無い乗務員のフォールバック）
  // 乗務員別 人件費率：歩合率（適用開始日つき）が設定されていればその率を常時適用（2026-08-27 一本化・トグル廃止）。
  // 率未設定・固定給（反映式は後日）は試算用率(labor)へフォールバック。
  const rateForCode = (code, iso) => laborRateFor(laborDrivers, code, iso, labor)
  const trips = rawTrips.map(normTrip)
  const tachos = rawTachos.map(normTacho)

  const byCode = {}
  let sumkm = 0, sumload = 0, sumfuel = 0; const plates = new Set()
  for (const t of tachos) {
    (byCode[t.driverCode] || (byCode[t.driverCode] = [])).push(t)
    sumkm += t.km; sumload += t.loaded; sumfuel += t.fuel; if (t.plate) plates.add(t.plate)
  }
  const rate = opt.rate ?? (sumkm ? sumfuel * DIESEL_YEN_PER_L / sumkm : 0)

  const self = trips.filter((t) => t.kind === 'own')
  const you = trips.filter((t) => t.kind === 'charter')

  // 便別 人件費（乗務員別率で算出・積日基準）。個別設定が無い乗務員は fare×labor（試算用率）。
  for (const x of self) x.jk = x.fare * rateForCode(x.driverCode, x.dLoad) // 率は積日基準（#5）

  // 突合: 乗務員コード×運行期間（開始〜終了の内側、期間が短いトリップ優先）。車両経費より前に実施し、車番(plate)を各運行へバックフィルする。
  let matched = 0
  for (const x of self) {
    x.trip = null; x.km = 0
    if (!x.driverCode || !x.d) continue
    let best = null
    for (const tp of (byCode[x.driverCode] || [])) {
      if (key(tp.sd) <= key(x.d) && key(x.d) <= key(tp.ed)) {
        const span = (tp.ed[0] === tp.sd[0]) ? (tp.ed[1] - tp.sd[1]) : 99
        if (best === null || span < best.span) best = { span, tp }
      }
    }
    if (best) { (best.tp.runs || (best.tp.runs = [])).push(x); x.trip = best.tp; matched++ }
  }
  // 車番の実データ化：日計側に車番が無い運行は、突合したデジタコの車番(plate)を採用（車両台帳マスタと突合する実車番）。
  for (const x of self) { if (!x.carNo && x.trip && x.trip.plate) x.carNo = x.trip.plate }

  // 車両経費：車両(車番)ごとに積→卸の営業日(土日祝除外)をユニオンで数え、営業日数×日額で計上。
  // 同一車両・同一日の重複運行はユニオンで1日に集約（＝同一ドライバー同日は1本化）。車番が無い運行はドライバー単位でフォールバック。
  const vehDays = {}, vehCls = {}, dvDays = {}
  for (const x of self) {
    const cn = x.carNo || ('NOCAR:' + x.driverCode)
    ;(vehCls[cn] || (vehCls[cn] = { large: 0, medium: 0 }))[vClass(x.vType)]++
    const days = bizDaysBetween(x.dLoad, x.dUnload)
    if (!days.length) continue
    const vs = vehDays[cn] || (vehDays[cn] = new Set())
    const dkey = x.driverCode + ' ' + cn
    const ds = dvDays[dkey] || (dvDays[dkey] = new Set())
    for (const d of days) { vs.add(d); ds.add(d) }
  }
  // 車種・日額リゾルバ：マスタ(opt.vehClass 車番→大型/中型・opt.vehRates 車番→日額)があれば実データ優先、無ければ自動判定＋既定日額(大型20,000/中型11,000)。
  const clsOf = (cn) => (opt.vehClass && opt.vehClass[cn]) || (vehCls[cn] && vehCls[cn].large >= vehCls[cn].medium ? 'large' : 'medium')
  const rateOf = (cn) => { const v = opt.vehRates && opt.vehRates[cn]; return (v != null && v !== '' && !isNaN(v)) ? Number(v) : VEHICLE_DAY_RATE[clsOf(cn)] }
  const vehCost = {}
  let sharyo = 0
  for (const cn in vehDays) { vehCost[cn] = vehDays[cn].size * rateOf(cn); sharyo += vehCost[cn] }
  if (!sharyo && opt.sharyoPool != null) sharyo = opt.sharyoPool
  // 乗務員へは「その車に乗った営業日数」の比で車両経費を配賦（各車の配賦は乗った乗務員の日数比＝配賦合計は車両経費に一致 → 乗務員別合計＝フリート車両経費）。稼働日＝営業日ユニオン。
  const vehDrvDays = {} // 車番→その車に乗った全乗務員の(日数)合計（＝配賦の分母。同日複数乗務員でも合計＝車両経費を保つ）
  for (const dk in dvDays) { const cn = dk.slice(dk.indexOf(' ') + 1); vehDrvDays[cn] = (vehDrvDays[cn] || 0) + dvDays[dk].size }
  const drvCar = {}, drvBiz = {}
  for (const dk in dvDays) {
    const sep = dk.indexOf(' '); const drv = dk.slice(0, sep), cn = dk.slice(sep + 1)
    const ds = dvDays[dk]; const set = drvBiz[drv] || (drvBiz[drv] = new Set()); for (const d of ds) set.add(d)
    const denom = vehDrvDays[cn] || 0; if (denom > 0) drvCar[drv] = (drvCar[drv] || 0) + vehCost[cn] * ds.size / denom
  }

  // トリップ内 走行kmを運賃按分
  for (const code in byCode) for (const tp of byCode[code]) {
    const runs = tp.runs || []; const tot = runs.reduce((s, r) => s + r.fare, 0) || 1
    for (const r of runs) r.km = tp.km * r.fare / tot
  }
  // 未突合の自社便は フリート平均km/¥ で推計（売上按分）
  const km_m = self.filter((x) => x.trip).reduce((s, x) => s + x.km, 0)
  const sale_m = self.filter((x) => x.trip).reduce((s, x) => s + x.fare, 0) || 1
  const kmpy = km_m ? km_m / sale_m : (opt.kmpy || 0) // デジタコ無い月は フリート平均km/¥ で推定
  for (const x of self) if (!x.trip) x.km = x.fare * kmpy

  // 客先別
  const C = {}
  const ens = (n) => (C[n] || (C[n] = { name: n, ji: 0, yo_uri: 0, yo_pay: 0, km: 0, meas: 0, tollBill: 0, jk: 0 }))
  for (const x of self) { const c = ens(x.customerName); c.ji += x.fare; c.km += x.km; c.tollBill += x.tollBill; c.jk += x.jk; if (x.trip) c.meas += x.fare }
  for (const x of you) { const c = ens(x.customerName); c.yo_uri += x.fare; c.yo_pay += x.charterCost }
  const custs = Object.values(C).map((c) => {
    const jinken = c.jk, fuel = c.km * rate, yo = c.yo_uri - c.yo_pay
    return { name: c.name, uri: Math.round(c.ji + c.yo_uri), ji: Math.round(c.ji), km: Math.round(c.km),
      fuel: Math.round(fuel), jinken: Math.round(jinken), yo_arari: Math.round(yo), contrib: Math.round(c.ji - jinken - fuel + yo) }
  }).sort((a, b) => b.contrib - a.contrib)

  // 乗務員別（コード→氏名はデジタコを正）
  const nameByCode = {}
  for (const t of tachos) if (t.driverCode && t.driverName && !nameByCode[t.driverCode]) nameByCode[t.driverCode] = t.driverName
  // 乗務員コードが空・突合先が無い便も、便自体に自由入力の氏名(driverName)が付いていれば拾う。
  // 何も名乗りが無いコード（多くは空文字）は「(未記入)」「(協力会社未設定)」と同じ流儀でラベルを出す。
  // 元は d.code をそのまま出していたため、コード無しの便がまとめて空欄の行になっていた（乗務員収支の最上位行が無名で出る不具合）。
  const driverNameHint = {}
  for (const x of self) if (x.driverName && !driverNameHint[x.driverCode]) driverNameHint[x.driverCode] = x.driverName
  const nmOf = (code) => (opt.names && opt.names[code]) || nameByCode[code] || driverNameHint[code] || code || '(乗務員コード未設定)'
  const D = {}
  for (const x of self) { const d = (D[x.driverCode] || (D[x.driverCode] = { code: x.driverCode, ji: 0, km: 0, n: 0, tollBill: 0, jk: 0 })); d.ji += x.fare; d.km += x.km; d.tollBill += x.tollBill; d.jk += x.jk; d.n++ }
  const drivers = Object.values(D).map((d) => {
    const jinken = d.jk, fuel = d.km * rate
    const carCost = Math.round(drvCar[d.code] || 0), days = drvBiz[d.code] ? drvBiz[d.code].size : 0, arari = d.ji - jinken - fuel
    return { ...d, days, name: nmOf(d.code), jinken: Math.round(jinken), fuel: Math.round(fuel),
      carCost, contrib: Math.round(arari), shushi: Math.round(arari - carCost) }
  }).sort((a, b) => b.contrib - a.contrib)
  const akaji = drivers.filter((d) => d.contrib < 0).length

  // 得意先×乗務員：得意先ごとに各乗務員の 自社便 運行/売上/人件費/燃料/粗利益。車両経費は車両単位のため得意先へは按分せず粗利益まで表示。
  const CD = {}
  for (const x of self) {
    const k = x.customerName + ' ' + x.driverCode
    const r = CD[k] || (CD[k] = { customer: x.customerName, code: x.driverCode, n: 0, uri: 0, km: 0, jk: 0 })
    r.n++; r.uri += x.fare; r.km += x.km; r.jk += x.jk
  }
  const drvTot = {} // 乗務員→{carCost,uri}：車両経費を得意先へ売上比で按分する用（合計＝乗務員別の車両経費）
  for (const d of drivers) drvTot[d.code] = { carCost: d.carCost, uri: d.ji }
  const custDrivers = Object.values(CD).map((r) => {
    const jinken = r.jk, fuel = r.km * rate, arari = r.uri - jinken - fuel
    const dt = drvTot[r.code] || { carCost: 0, uri: 0 }
    const carCost = dt.uri > 0 ? Math.round(dt.carCost * r.uri / dt.uri) : 0
    return { customer: r.customer, driver: nmOf(r.code),
      n: r.n, uri: Math.round(r.uri), jinken: Math.round(jinken), fuel: Math.round(fuel), arari: Math.round(arari), carCost, shushi: Math.round(arari - carCost) }
  }).sort((a, b) => a.customer.localeCompare(b.customer, 'ja') || b.arari - a.arari)

  // 車両別（突合できたトリップ＝車番が確定した分。デジタコの走行/実車/給油＋按分売上）
  const V = {}
  for (const code in byCode) for (const tp of byCode[code]) {
    const k = tp.plate || '(不明)'
    const v = (V[k] || (V[k] = { plate: tp.plate, name: tp.name || tp.plate, km: 0, loaded: 0, fuelL: 0, uri: 0, days: 0, tollBill: 0, jk: 0 }))
    v.km += tp.km; v.loaded += tp.loaded; v.fuelL += tp.fuel; v.days++
    v.uri += (tp.runs || []).reduce((s, r) => s + r.fare, 0)
    v.jk += (tp.runs || []).reduce((s, r) => s + (r.jk || 0), 0)
    v.tollBill += (tp.runs || []).reduce((s, r) => s + (r.tollBill || 0), 0)
  }
  const vehicles = Object.values(V).map((v) => {
    const fuel = v.km * rate, jinken = v.jk
    return { ...v, km: Math.round(v.km), loaded: Math.round(v.loaded), fuelL: Math.round(v.fuelL), uri: Math.round(v.uri),
      fuel: Math.round(fuel), jinken: Math.round(jinken), contrib: Math.round(v.uri - jinken - fuel),
      jissharitsu: v.km ? Math.round(v.loaded / v.km * 1000) / 1000 : 0, kmL: v.fuelL ? Math.round(v.km / v.fuelL * 100) / 100 : 0 }
  }).sort((a, b) => b.uri - a.uri)

  // 利用運送（傭車）別 協力会社サマリ
  const P = {}
  for (const x of you) {
    const k = x.partnerName || '(協力会社未設定)'
    const p = P[k] || (P[k] = { name: k, n: 0, uri: 0, pay: 0 })
    p.n++; p.uri += x.fare; p.pay += x.charterCost
  }
  const partners = Object.values(P).map((p) => ({
    name: p.name, n: p.n, uri: Math.round(p.uri), pay: Math.round(p.pay),
    arari: Math.round(p.uri - p.pay), rate: p.uri ? Math.round((p.uri - p.pay) / p.uri * 1000) / 10 : 0,
  })).sort((a, b) => b.arari - a.arari)

  // 便明細（1便ごと）：発→着・運賃(単価)・原価・粗利。係数は集計表と同一（人件費率labor・当月¥/km rate）。車両経費は日次固定費のため便別には含めない。
  // nmOf は §乗務員別 の直後で定義済み（コード無し便への氏名フォールバックを共有するため）
  // 便別 車両経費：各車の営業日ごとの日額(大型20,000/中型11,000)を「その日・その車を使った便」で等分。∴Σ(便別車両経費)＝§17フリート車両経費。
  const occ = {} // 'cn|day' → その日その車の便数
  const sdCache = new Map()
  for (const x of self) {
    const cn = x.carNo || ('NOCAR:' + x.driverCode)
    const days = bizDaysBetween(x.dLoad, x.dUnload)
    sdCache.set(x, { cn, days })
    for (const d of days) { const k = cn + '|' + d; occ[k] = (occ[k] || 0) + 1 }
  }
  const meisai = []
  for (const x of self) {
    const jinken = x.jk, fuel = x.km * rate, arari = x.fare - jinken - fuel
    const sd = sdCache.get(x); const dr = rateOf(sd.cn)
    let carCost = 0; for (const d of sd.days) carCost += dr / (occ[sd.cn + '|' + d] || 1) // 日額をその日の便で等分
    const shushi = arari - carCost
    meisai.push({ date: x.dLoad || '', kind: 'own', customer: x.customerName, who: nmOf(x.driverCode) || x.driverName || x.driverCode,
      from: x.from, to: x.to, vType: x.vType, days: sd.days.length, fare: Math.round(x.fare), jinken: Math.round(jinken), fuel: Math.round(fuel),
      charterCost: 0, arari: Math.round(arari), carCost: Math.round(carCost), shushi: Math.round(shushi),
      margin: x.fare ? Math.round(arari / x.fare * 1000) / 10 : 0, smargin: x.fare ? Math.round(shushi / x.fare * 1000) / 10 : 0, matched: !!x.trip })
  }
  for (const x of you) {
    const arari = x.fare - x.charterCost
    meisai.push({ date: x.dLoad || '', kind: 'charter', customer: x.customerName, who: x.partnerName || x.driverName || '',
      from: x.from, to: x.to, vType: x.vType, days: 0, fare: Math.round(x.fare), jinken: 0, fuel: 0,
      charterCost: Math.round(x.charterCost), arari: Math.round(arari), carCost: 0, shushi: Math.round(arari),
      margin: x.fare ? Math.round(arari / x.fare * 1000) / 10 : 0, smargin: x.fare ? Math.round(arari / x.fare * 1000) / 10 : 0, matched: false })
  }

  // 日別乗務員：乗務員×積日(運行日)で 売上・原価・粗利＋車両経費(その日の車種で 大型¥20,000/中型¥11,000 を1日分＝日ごと1台日)・収支。
  const DD = {}
  for (const x of self) {
    const d = x.dLoad; if (!d) continue
    const k = x.driverCode + '|' + d
    const r = DD[k] || (DD[k] = { code: x.driverCode, date: d, n: 0, uri: 0, km: 0, large: 0, cars: new Set(), jk: 0 })
    r.n++; r.uri += x.fare; r.km += x.km; r.jk += x.jk
    if (vClass(x.vType) === 'large') r.large++
    if (x.carNo) r.cars.add(x.carNo)
  }
  const driverDays = Object.values(DD).map((r) => {
    const jinken = r.jk, fuel = r.km * rate, arari = r.uri - jinken - fuel
    // その日の代表車両（大型優先）で実日額を採用。車番不明時は当日の車種で既定日額。
    let cn = null; for (const c of r.cars) { if (!cn) cn = c; if (clsOf(c) === 'large') { cn = c; break } }
    const cls = cn ? clsOf(cn) : (r.large > 0 ? 'large' : 'medium'), carCost = cn ? rateOf(cn) : VEHICLE_DAY_RATE[cls], shushi = arari - carCost
    return { date: r.date, driver: nmOf(r.code), cars: [...r.cars].join(','), cls: cls === 'large' ? '大型' : '中型', n: r.n, km: Math.round(r.km),
      uri: Math.round(r.uri), jinken: Math.round(jinken), fuel: Math.round(fuel), arari: Math.round(arari),
      carCost, shushi: Math.round(shushi), smargin: r.uri ? Math.round(shushi / r.uri * 1000) / 10 : 0 }
  }).sort((a, b) => a.smargin - b.smargin)

  // 配車担当別（trips.staff）：自社便＋傭車を担当で集計。自社便=売上/人件費/燃料、傭車=取り分。車両経費はフリート総額を担当の自社便売上比で配賦。収支＝粗利益−車両経費。
  const SF = {}
  const ensSF = (k) => SF[k] || (SF[k] = { staff: k, n: 0, uri: 0, km: 0, yoUri: 0, yoPay: 0, jk: 0 })
  for (const x of self) { const s = ensSF(x.staff || '(未設定)'); s.n++; s.uri += x.fare; s.km += x.km; s.jk += x.jk }
  for (const x of you) { const s = ensSF(x.staff || '(未設定)'); s.n++; s.yoUri += x.fare; s.yoPay += x.charterCost }
  const selfUriTot = self.reduce((a, x) => a + x.fare, 0) || 1
  const staffAgg = Object.values(SF).map((s) => {
    const jinken = s.jk, fuel = s.km * rate, yoArari = s.yoUri - s.yoPay
    const arari = s.uri - jinken - fuel + yoArari
    const carCost = Math.round(sharyo * s.uri / selfUriTot)
    return { staff: s.staff, n: s.n, uri: Math.round(s.uri), yoArari: Math.round(yoArari),
      jinken: Math.round(jinken), fuel: Math.round(fuel), arari: Math.round(arari), carCost, shushi: Math.round(arari) - carCost }
  }).sort((a, b) => b.arari - a.arari)

  const ji_uri = Math.round(self.reduce((s, x) => s + x.fare, 0))
  const km_tot = Math.round(self.reduce((s, x) => s + x.km, 0))
  const yo_uri = Math.round(you.reduce((s, x) => s + x.fare, 0))
  const yo_pay = Math.round(you.reduce((s, x) => s + x.charterCost, 0))
  const fuel_tot = custs.reduce((s, c) => s + c.fuel, 0)
  const contrib_tot = custs.reduce((s, c) => s + c.contrib, 0)
  const jinken_tot = Math.round(self.reduce((s, x) => s + (x.jk || 0), 0))
  return {
    summary: {
      ji_uri, yo_uri, yo_pay, yo_arari: yo_uri - yo_pay, tot_uri: ji_uri + yo_uri,
      km: km_tot, fuel: fuel_tot, jinken: jinken_tot, contrib: contrib_tot,
      laborRate: labor, // 試算用率＝個別設定が無い乗務員のフォールバック（UI表示用）
      sharyo, zensha: contrib_tot - sharyo, // 全社収支（車両経費プール控除）
      self_runs: self.length, you_runs: you.length, drivers: Object.keys(D).length, akaji,
      match_rate: self.length ? Math.round(matched / self.length * 1000) / 1000 : 0, n_cust: custs.length,
      jissharitsu: sumkm ? Math.round(sumload / sumkm * 1000) / 1000 : (opt.jissharitsu ? Math.round(opt.jissharitsu * 1000) / 1000 : 0),
      kmL: sumfuel ? Math.round(sumkm / sumfuel * 100) / 100 : (opt.kmL ? Math.round(opt.kmL * 100) / 100 : 0),
      unit_plates: plates.size, rate: Math.round(rate * 100) / 100,
      estimated: tachos.length === 0 && !!(opt.kmpy || opt.rate), // 実デジタコ無し→推定月
    },
    vehMaster: Object.keys(vehDays).map((cn) => ({ carNo: cn.startsWith('NOCAR:') ? '' : cn, key: cn, driverFallback: cn.startsWith('NOCAR:') ? cn.slice(6) : '', days: vehDays[cn].size, klass: clsOf(cn), dayRate: rateOf(cn), cost: vehCost[cn] })).sort((a, b) => b.cost - a.cost),
    custs, drivers, vehicles, partners, custDrivers, meisai, driverDays, staffAgg,
  }
}
